import { useEffect, useRef, useState } from "react";
import { MultiSelect } from "react-multi-select-component";
import DateTimePicker from "react-datetime-picker";
import classnames from "classnames";
import PageHeader from "components/PageHeader";
import RadioButton from "components/RadioButton";
import CustomTimePicker from "components/CustomTimePicker";
import FormField, { STYLE } from "components/FormField";
import Button, { SIZE as VIEW, COLOR } from "components/Button";
import trucksService from "services/trucks.service";
import {
  formattedCanadianProvinces,
  formattedUSStates,
  availHeight,
  destinationRenderer,
  preparedDestination,
  preparedSelected,
} from "./helpers";

import styles from "./EditModal.module.scss";
import { Loading } from "components/assets/Icons";

const EditModal = ({ data, setShowModal, setSuccess }) => {
  const inputRef = useRef();
  const autoCompleteRef = useRef(null);
  const [status, setStatus] = useState(data?.item?.availability_status);
  const [value, onChange] = useState(new Date());
  const [customTime, setCustomTime] = useState(false);
  const [destinationNotes, setdestinationNotes] = useState(data?.item.destination?.note);
  const [maxWaitTime, setMaxWaitTime] = useState(
    data?.item?.max_wait_time_for_pickup ?? 24,
  );
  const [loading, setLoading] = useState(false);

  const handleChangeNotes = (e) => {
    setdestinationNotes(e.target.value);
    setDestinationErr("");
  };

  const handleChangeMaxWaitTime = (e) => {
    const n = parseInt(e.target.value, 10);
    setMaxWaitTime(Number.isNaN(n) ? "" : Math.max(0, n));
  };

  // Status
  const handleChange = (event) => {
    setStatus(event.target.value);
  };

  const isChecked = (value) => status === value;

  // Location
  // Initialize Autocomplete with default value
  useEffect(() => {
    const options = {
      componentRestrictions: { country: ["us", "ca"] },
      fields: ["address_components", "geometry"],
      strictBounds: false,
      types: ["geocode"],
    };
    const defaultLatLng = {
      lat: data?.item?.location?.latitude,
      lng: data?.item?.location?.longitude,
    }; // Example coordinates
    // Reverse geocode default coordinates to obtain address
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: defaultLatLng }, (results, status) => {
      if (status === "OK") {
        if (results[0]) {
          fillInAddress(results[0]);
          // Set default value to the input field as human-readable address
          inputRef.current.value = results[0].formatted_address;
        } else {
          console.error("No results found");
        }
      } else {
        console.error("Geocoder failed due to: " + status);
      }
    });
    autoCompleteRef.current = new window.google.maps.places.Autocomplete(
      inputRef.current,
      options,
    );

    autoCompleteRef.current.addListener("place_changed", (e) => {
      const place = autoCompleteRef.current.getPlace();

      if (!place.geometry || !place.geometry.location) {
        // User entered the name of a Place that was not suggested and
        // pressed the Enter key, or the Place Details request failed.
        return;
      }
      if (place.geometry.viewport || place.geometry.location) {
        fillInAddress(place);
      }
    });
  }, []);

  const fillInAddress = (place) => {
    let city, state, zip;
    place.address_components.forEach(function (component) {
      if (component.types.includes("locality")) {
        city = component.long_name;
      } else if (component.types.includes("administrative_area_level_1")) {
        state = component.long_name;
      } else if (component.types.includes("postal_code")) {
        zip = component.long_name;
      }
    });

    let latitude = place.geometry.location.lat();
    let longitude = place.geometry.location.lng();

    let result = {
      latitude: latitude,
      longitude: longitude,
      state: state,
      city: city,
      zip_code: zip ? zip : null,
    };
    setFetchData({ ...fetchData, location: result });
    setLocationError(false);
  };

  // destination select
  const [selected, setSelected] = useState({
    destination: preparedSelected(data?.item?.destination?.states?.split(",")),
    list: [...formattedUSStates, ...formattedCanadianProvinces],
  });

  // data for request
  const [fetchData, setFetchData] = useState({
    id: data?.item?.id,
    availability_status: data?.item?.availability_status,
    location: "",
    available_datetime: value,
    destination: null,
    destination_note: data?.item?.destination?.note,
  });

  //for valid update of data for fetch
  useEffect(() => {
    setFetchData({
      ...fetchData,
      available_datetime: value,
      destination: preparedDestination(selected.destination),
      destination_note: destinationNotes,
      availability_status: status,
    });
  }, [value, selected.destination, destinationNotes, status]);
  
  const toLocalISOString = (date) => {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  };  
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Cleared/invalid field falls back to the 24h default; an explicit 0 is a
    // valid value (immediate pickup) and is preserved, not coerced to 24.
    const parsedMaxWait = parseInt(maxWaitTime, 10);
    const finalData = {
      ...fetchData,
      available_datetime: value ? toLocalISOString(value) : null,
      max_wait_time_for_pickup: Number.isNaN(parsedMaxWait)
        ? 24
        : Math.max(0, parsedMaxWait),
    };

    if (validate(finalData)) {
      setLoading(true);
      trucksService
        .updateStatusAndLocation(finalData)
        .then((res) => {
          if (res.status === 200) {
            setSuccess("Edit Stalus & Location was succesfull");
            setTimeout(() => {
              setShowModal(false);
            }, 100);
          }
        })
        .catch((e) => {
          console.error("Edit modal error:", e.response?.data);
          setLoading(false);
          // TODO: validate errors
        });
    }
  };

  //validation
  const [locationError, setLocationError] = useState("");
  const [destinationErr, setDestinationErr] = useState("");

  const validate = (data) => {
    if (!data.location) {
      setLocationError(true);
      return;
    // } else if (!destinationNotes) {
    //   setDestinationErr("This field is required");
    //   return;
    // } else if (destinationNotes.length > 250) {
    //   setDestinationErr("No more symbols can be input in the field");
    //   return;
    } else {
      return true;
    }
  };

  return (
    <div className={classnames(styles.wrapper, availHeight < 850 && styles.scrollbar)}>
      <PageHeader title="Edit Status & Location" className={styles.header} />

      <form className={classnames(styles.form)} onSubmit={handleSubmit}>
        <p className={styles.unit}>UNIT {data?.item.unit}</p>

        <div className={styles.group}>
          <span className={styles.label}>
            Status <span className={styles.red}>*</span>
          </span>

          <RadioButton
            id="Available"
            name="Available"
            value="Available"
            text="Available"
            onChange={handleChange}
            checked={isChecked("Available")}
          />

          <RadioButton
            id="En route"
            name="En route"
            value="En route"
            text="En Route"
            onChange={handleChange}
            checked={isChecked("En route")}
          />

          <RadioButton
            id="Not available"
            name="Not available"
            value="Not available"
            text="Not Available"
            onChange={handleChange}
            checked={isChecked("Not available")}
          />
        </div>

        <div className={styles.group}>
          <span className={styles.label}>
            Location <span className={styles.red}>*</span>
          </span>
          <input
            placeholder="type your location"
            ref={inputRef}
            className={classnames(styles.input, locationError && styles.error)}
          />
          {/* {locationError && <p className={styles.input_error}>{locationError}</p>} */}
          {locationError && <p className={styles.input_error}>This field is required</p>}
        </div>
        {/* This field is required. */}

        <div className={styles.group}>
          <span className={styles.label}>
            Date & Time <span className={styles.red}>*</span>
          </span>
          <DateTimePicker
            onChange={onChange}
            value={value}
            format="MM.dd.y H:mm a"
            calendarIcon={null}
            clearIcon={null}
            onClockOpen={() => {
              setCustomTime(true);
            }}
          />
          {customTime && (
            <div className={styles.timepicker_wrapper}>
              <CustomTimePicker
                data={value}
                onChange={onChange}
                setIsOpen={setCustomTime}
              />
            </div>
          )}
        </div>

        <div className={styles.group}>
          <label htmlFor="max_wait_time_for_pickup" className={styles.label}>
            Max wait time for pick-up (hours)
          </label>
          <input
            type="number"
            id="max_wait_time_for_pickup"
            name="max_wait_time_for_pickup"
            className={styles.input}
            min={0}
            value={maxWaitTime}
            onChange={handleChangeMaxWaitTime}
          />
        </div>

        <div className={styles.group}>
          <span className={styles.label}>Preferences </span>
          <MultiSelect
            className={styles.select}
            options={selected.list}
            value={selected.destination}
            onChange={(e) => setSelected({ ...selected, destination: e })}
            valueRenderer={destinationRenderer}
          />
        </div>

        <FormField
          fieldName="notes"
          labelText="Destination Notes "
          style={STYLE.POPUP}
          onChange={handleChangeNotes}
          value={destinationNotes}
          // error={destinationErr ? true : false}
          // errorMessage={destinationErr}
          className={styles.errorMessage}
        />
        {/* <span
          className={styles.red}
          style={{ position: "absolute", bottom: "115px", left: "130px" }}
        >
          *
        </span> */}

        <div className={styles.buttongroup}>
          <Button
            size={VIEW.MIDDLE}
            color={COLOR.NONE}
            onClick={() => setShowModal(false)}
          >
            Back
          </Button>
          <Button size={VIEW.MIDDLE} type="submit">
            {loading ? <Loading /> : "Save"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditModal;
